/**
 * Enhanced logging utility that wraps console methods and integrates with Sentry.
 * Provides structured logging with automatic Sentry breadcrumbs and error reporting,
 * while maintaining backward compatibility with console usage patterns.
 * Update when modifying logging behavior, Sentry integration, or error filtering logic.
 */

import {
  addBreadcrumb,
  captureException,
  captureMessage,
  isSentryInitialized,
} from '@/services/sentryService';

/**
 * Gets current route information from window location.
 */
const getCurrentRoute = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }
  return window.location?.pathname || 'unknown';
};

/**
 * Checks if an error should be filtered out (non-critical errors that shouldn't be reported).
 */
const shouldFilterError = (error: Error | string, source?: string): boolean => {
  if (source && typeof source === 'string') {
    const lowerSource = source.toLowerCase();
    if (
      lowerSource.includes('connect.facebook.net') ||
      lowerSource.startsWith('iabjs://')
    ) {
      return true;
    }
  }

  const message =
    typeof error === 'string'
      ? error
      : error?.message || error?.toString() || '';
  if (typeof message === 'string') {
    const lowerMessage = message.toLowerCase();
    if (
      lowerMessage.includes('facebook') ||
      lowerMessage.includes('meta pixel') ||
      lowerMessage.includes('fbq') ||
      lowerMessage.includes('java object is gone') ||
      lowerMessage.includes('enablebuttonsclickedmetadatalogging')
    ) {
      return true;
    }
  }

  if (
    typeof error !== 'string' &&
    error?.stack &&
    typeof error.stack === 'string'
  ) {
    const stack = error.stack.toLowerCase();
    if (stack.includes('connect.facebook.net') || stack.includes('iabjs://')) {
      return true;
    }
  }

  return false;
};

interface LogContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  [key: string]: unknown;
}

interface Logger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(
    messageOrError: string | Error,
    contextOrError?: LogContext | Error
  ): void;
  log(message: string, context?: LogContext): void;
}

/**
 * Creates a logger instance with methods that wrap console and integrate with Sentry.
 */
const createLogger = (): Logger => {
  const route = getCurrentRoute();

  return {
    debug(message: string, context: LogContext = {}) {
      if (!import.meta.env.PROD) {
        /* eslint-disable no-console -- logger abstraction intentionally wraps console.debug */
        Object.keys(context).length > 0
          ? console.debug(message, context)
          : console.debug(message);
        /* eslint-enable no-console */
      }

      if (!import.meta.env.PROD && isSentryInitialized()) {
        try {
          addBreadcrumb(message, { ...context, route }, 'console', 'debug');
        } catch {
          // Non-blocking: silently fail
        }
      }
    },

    info(message: string, context: LogContext = {}) {
      /* eslint-disable no-console -- logger abstraction intentionally wraps console.info */
      Object.keys(context).length > 0
        ? console.info(message, context)
        : console.info(message);
      /* eslint-enable no-console */

      if (isSentryInitialized()) {
        try {
          addBreadcrumb(message, { ...context, route }, 'console', 'info');
        } catch {
          // Non-blocking: silently fail
        }
      }
    },

    warn(message: string, context: LogContext = {}) {
      Object.keys(context).length > 0
        ? console.warn(message, context)
        : console.warn(message);

      if (isSentryInitialized()) {
        try {
          const { tags = {}, extra = {}, ...restContext } = context;
          const breadcrumbData = { ...restContext, route, ...extra };

          addBreadcrumb(message, breadcrumbData, 'console', 'warning');

          captureMessage(message, {
            level: 'warning',
            tags: { ...tags, route },
            extra: { ...extra, ...restContext, route },
          });
        } catch {
          // Non-blocking: silently fail
        }
      }
    },

    error(
      messageOrError: string | Error,
      contextOrError: LogContext | Error = {}
    ) {
      const isError = messageOrError instanceof Error;
      const error = isError ? messageOrError : null;
      const message = isError
        ? messageOrError.message || 'Error occurred'
        : messageOrError;
      const context: LogContext =
        contextOrError instanceof Error ? {} : contextOrError;

      const hasContext = Object.keys(context).length > 0;
      if (error) {
        hasContext ? console.error(error, context) : console.error(error);
      } else {
        hasContext ? console.error(message, context) : console.error(message);
      }

      if (isSentryInitialized()) {
        try {
          const { tags = {}, extra = {}, ...restContext } = context;
          const breadcrumbData = { ...restContext, route, ...extra };

          addBreadcrumb(message, breadcrumbData, 'console', 'error');

          if (error && shouldFilterError(error)) {
            return;
          }

          if (error) {
            captureException(error, {
              level: 'error',
              tags: { ...tags, route },
              extra: { ...extra, ...restContext, route },
            });
          } else {
            captureMessage(message, {
              level: 'error',
              tags: { ...tags, route },
              extra: { ...extra, ...restContext, route },
            });
          }
        } catch {
          // Non-blocking: silently fail
        }
      }
    },

    log(message: string, context: LogContext = {}) {
      this.info(message, context);
    },
  };
};

// Create and export a singleton logger instance
const logger = createLogger();

export default logger;
